/**
 * api/file.ts —— 文件能力 + 「整合文件」三段式（设计文档 5.1 / D14）
 *
 * 这是 M4 里最有分量的一个 API，因为它落的是用户最初提出的那个需求：
 *   「把王二发来的文件整理一下」
 *
 * 三段式（关键设计）：
 *   ① 收集  —— 本地确定性遍历，不碰 AI。任何时刻都能跑，结果稳定。
 *   ② 落库  —— 写一条 file_digests 记录，**此时 summary 还是空的**。
 *   ③ 摘要  —— 把清单喂给 AI 生成一段人话，回填 summary 字段。
 *
 * 为什么三段要分开、且第 ③ 步是异步补的：
 *   如果「收集 + AI 摘要」揉成一步，AI 一挂这个功能就整个不可用，
 *   而且用户会以为"整理"没成功 —— 实际上文件清单早就有了。
 *   拆开之后：第 ①② 步永远成功，记录一定落库；
 *   第 ③ 步失败也只是摘要那句是空的，记录本身完好。
 *   用户下次还可以说「把那个摘要补上」，不需要重新扫一遍。
 *
 * 本文件只负责 ①②（收集 + 落库）。
 * ③（AI 摘要）由 feedback 层的 `summarizeFiles()` 在拿到记录后补 —— 见 ai/summarize.ts。
 * 这样 file.ts 完全不依赖 AI 模块，AI 挂了它照样工作。
 */

import { filesRepo, formatSize, categorize } from '@/data/repo.files';
import { sessionsRepo } from '@/data/repo.sessions';
import { contactsRepo } from '@/data/repo.contacts';
import { handles } from '../handles';
import { deviceRuntime } from '../device-runtime';
import { pushOverlay } from '@/core/ui-store';
import { RuntimeError, type ObjectDef } from '../registry';

const TYPE_LABEL: Record<string, string> = {
  image: '图片',
  doc: '文档',
  sheet: '表格',
  pdf: 'PDF',
  voice: '语音',
  video: '视频',
  archive: '压缩包',
  other: '其他',
};

/** 按联系人姓名解析出会话，找不到就直接报错（不自动建空会话） */
async function sessionOf(contactName: string): Promise<{ id: string; who: string }> {
  const all = await contactsRepo.list();
  const target = contactName.trim();
  const c =
    all.find((x) => x.name === target) ??
    all.find((x) => x.remark === target) ??
    all.find((x) => x.pinyin === target.toLowerCase()) ??
    all.find((x) => x.name.includes(target) || x.remark.includes(target));

  if (!c) throw new RuntimeError(`联系人里没有「${target}」`, 'NameError');

  const list = await sessionsRepo.listByContact(c.id);
  if (list.length === 0) {
    throw new RuntimeError(`跟「${c.name}」还没有聊天记录，所以没有文件可以整理`, 'RuntimeError');
  }
  return { id: list[0].id, who: c.name };
}

export const fileApi = {
  name: 'file',
  desc: '聊天里的文件（图片、文档、语音等），以及「把文件整理成摘要」的能力',
  userDesc: '整理聊天里的文件 —— 看某个人的文件、把文件汇总成一份摘要',
  methods: [
    {
      name: 'listInChat',
      kind: 'query',
      desc: '列出某联系人聊天里收到的全部文件。用户问「xxx 给我发过哪些文件」时用这个。',
      params: [{ name: 'name', type: 'string' as const, desc: '联系人姓名', required: true }],
      successText: (args: unknown[]) => `已列出与「${String(args[0])}」的文件`,
      run: async (args: unknown[]) => {
        const { id, who } = await sessionOf(String(args[0]));
        const items = await filesRepo.collectInSession(id);
        return {
          value: items.map((it) => ({
            name: it.name,
            size: formatSize(it.size),
            type: TYPE_LABEL[categorize(it.mime)] ?? '其他',
          })),
          text: `与「${who}」的文件共 ${items.length} 个`,
        };
      },
    },
    {
      name: 'listAll',
      kind: 'query',
      desc: '列出全部聊天里的所有文件。',
      params: [],
      successText: '已列出全部文件',
      run: async () => {
        const all = await filesRepo.listAll();
        return { value: { count: all.length, items: all.map((f) => f.name) } };
      },
    },
    {
      name: 'summarize',
      desc:
        '把某联系人聊天里的文件整理成一份摘要（会存档，之后可以再打开）。' +
        '用户说「把 xxx 发的文件整理一下」「汇总 xxx 的文件」时用这个。',
      params: [{ name: 'name', type: 'string' as const, desc: '联系人姓名', required: true }],
      successText: (args: unknown[]) => `已整理「${String(args[0])}」的文件`,
      run: async (args: unknown[]) => {
        const { id, who } = await sessionOf(String(args[0]));

        // ① 收集（确定性）
        const items = await filesRepo.collectInSession(id);
        if (items.length === 0) {
          throw new RuntimeError(`「${who}」还没有发过文件，没什么可整理的`, 'RuntimeError');
        }

        // ② 落库（summary 留空，等 ③ 补）
        const title = `${who}的 ${items.length} 个文件`;
        const digest = await filesRepo.createDigest(id, title, items);

        // 登记句柄，让后续语句能引用这份摘要
        const handleId = handles.add('digest', `${title}（${formatSize(digest.totalSize)}）`, {
          digestId: digest.id,
        });

        return {
          value: { handleId, digestId: digest.id, count: items.length },
          text: `已整理「${who}」的 ${items.length} 个文件，共 ${formatSize(digest.totalSize)}`,
        };
      },
    },
    {
      name: 'listDigests',
      kind: 'query',
      desc: '列出已经整理过的文件摘要。用户问「之前整理过哪些文件」时用这个。',
      params: [],
      successText: '已列出文件摘要',
      run: async () => {
        const list = await filesRepo.listDigests();
        return {
          value: list.map((d) => ({
            handleId: handles.listOf('digest').find((h) => {
              const p = h.payload as { digestId?: string } | undefined;
              return p?.digestId === d.id;
            })?.id,
            title: d.title,
            count: d.itemCount,
            size: formatSize(d.totalSize),
            hasSummary: Boolean(d.summary),
          })),
        };
      },
    },
    {
      name: 'openDigest',
      desc: '在界面上打开一份文件整理结果。',
      params: [
        { name: 'handle', type: 'string' as const, desc: '摘要编号，如 digest_1', required: true },
      ],
      successText: '已打开文件整理结果',
      run: async (args: unknown[]) => {
        const h = handles.get(String(args[0]));
        const digestId = (h?.payload as { digestId?: string } | undefined)?.digestId;
        if (!digestId) throw new RuntimeError(`找不到编号为「${String(args[0])}」的整理结果`, 'NameError');
        pushOverlay({ kind: 'digest', refId: digestId });
        return {};
      },
    },
    {
      name: 'removeDigest',
      desc: '删除一份文件整理结果。注意：只删摘要，原始消息和文件不受影响。',
      params: [{ name: 'handle', type: 'string' as const, desc: '摘要编号', required: true }],
      successText: '已删除文件整理结果',
      run: async (args: unknown[]) => {
        const handleId = String(args[0]);
        const h = handles.get(handleId);
        const digestId = (h?.payload as { digestId?: string } | undefined)?.digestId;
        if (!digestId) throw new RuntimeError(`找不到编号为「${handleId}」的整理结果`, 'NameError');
        await filesRepo.removeDigest(digestId);
        handles.remove(handleId);
        return {};
      },
    },
    {
      name: 'sendPhoto',
      desc:
        '把刚拍的照片发给某个联系人。用户说「把这张照片发给 xxx」「拍一张发给 xxx」时用这个。' +
        '不带编号时用最近拍的那张。',
      params: [
        { name: 'name', type: 'string' as const, desc: '收件联系人姓名', required: true },
        { name: 'handle', type: 'string' as const, desc: '照片编号，如 image_1（可选，默认最近一张）', required: false },
      ],
      successText: (args: unknown[]) => `已把照片发给「${String(args[0])}」`,
      run: async (args: unknown[]) => {
        const [contactName, handleId] = args as [string, string | undefined];
        const entry = handleId ? handles.get(handleId) : handles.latest('image');
        const blob =
          (entry?.payload as { blob?: Blob } | undefined)?.blob ?? deviceRuntime.camera.lastPhoto;
        if (!blob) {
          throw new RuntimeError('还没有拍过照片。可以先说「拍一张」。', 'RuntimeError');
        }

        const { id, who } = await sessionOf(contactName);
        const fileName = (entry?.payload as { name?: string } | undefined)?.name ?? '照片.png';

        // 跨表写入（messages + files）统一走 repo —— UI 路径也走同一个方法，
        // 避免"两条路径各自实现、其中一条忘了写 files 表"这类坏数据。
        await filesRepo.attachToMessage({
          sessionId: id,
          sender: 'me',
          name: fileName,
          blob,
          mime: blob.type || 'image/png',
          kind: 'image',
        });

        return { text: `已把照片发给「${who}」` };
      },
    },
  ],
} satisfies ObjectDef;

/** 供 ai 摘要模块复用：读某条 digest 的完整内容 */
export async function readDigest(digestId: string) {
  return filesRepo.getDigest(digestId);
}

/** 供 ai 摘要模块复用：回填 summary */
export async function writeDigestSummary(digestId: string, summary: string): Promise<void> {
  await filesRepo.setDigestSummary(digestId, summary);
}

/** 供 ai 摘要模块复用：把清单渲染成给模型看的文本 */
export async function renderDigestForAI(digestId: string): Promise<string | undefined> {
  const d = await filesRepo.getDigest(digestId);
  if (!d) return undefined;
  const byTypeText = Object.entries(d.byType)
    .map(([k, v]) => `${TYPE_LABEL[k] ?? k} ${v} 个`)
    .join('、');
  const lines = d.items.map((it, i) => {
    const when = it.createdAt ? new Date(it.createdAt).toLocaleString('zh-CN') : '';
    return `${i + 1}. ${it.name}（${TYPE_LABEL[categorize(it.mime)] ?? '其他'}，${formatSize(it.size)}）${when ? ` · ${when}` : ''}`;
  });
  return [`共 ${d.itemCount} 个文件，总 ${formatSize(d.totalSize)}。构成：${byTypeText}。`, '', ...lines].join('\n');
}

/** 供调试页复用 */
export { formatSize, categorize };
